import { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { listHolidays } from '@/api/holidays';
import { getAdminBookingDashboard, getSosInbox } from '@/api/booking';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Fingerprint, ClipboardList, ClipboardCheck,
  Briefcase, MapPin, CalendarDays, CalendarRange, ShieldCheck,
  Users, BarChart2, PanelLeftClose, PanelLeftOpen, Inbox, Settings, X, CalendarClock, HelpCircle, GitBranch, Megaphone, Mail,
  Search, BookOpen, Compass, ListChecks, Siren, Umbrella, Scale, SlidersHorizontal, Activity, Laptop, Boxes,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { useSidebar } from '@/contexts/SidebarContext';
import { APP_VERSION } from '@/data/changelog';
import { usePendingApprovalCount, PENDING_REQUESTS_PATH } from '@/hooks/usePendingApprovalCount';

const EASE = 'cubic-bezier(0.4, 0, 0.2, 1)';
const DUR = '0.22s';

interface NavItem {
  label: string;
  to: string;
  icon: React.ElementType;
  /** Item visible to all authenticated users if omitted. */
  permission?: string;
  /** Also listed in the HR view. Without it the page is Admin-only. */
  hr?: boolean;
}

const MAIN_NAV: NavItem[] = [
  { label: 'Home',        to: '/andone/dashboard',    icon: LayoutDashboard },
  { label: 'My Records',  to: '/andone/my-records',   icon: ClipboardList },
  { label: 'My Calendar', to: '/andone/my-calendar',  icon: CalendarRange },
  { label: 'My Requests', to: '/andone/my-requests',  icon: ClipboardCheck },
  { label: 'My Leaves',   to: '/andone/my-leaves',    icon: Umbrella },
  { label: 'Leave Types', to: '/andone/leave-types',  icon: BookOpen },
  { label: 'Holidays',    to: '/andone/holidays',     icon: CalendarDays },
  { label: 'My Inventory', to: '/andone/my-inventory', icon: Laptop },
  { label: 'My Passkey',  to: '/andone/passkeys',     icon: Fingerprint },
  { label: 'Org Chart',   to: '/andone/org-chart',    icon: GitBranch },
];

/**
 * What an approver needs who is not staff. A reporting manager (or anyone with
 * self/global approve) has to reach their team's requests and attendance, but
 * that is not a reason to hand them the HR view — the role decides that.
 *
 * Both pages scope themselves server-side: `getApprovableUserIds` narrows the
 * request list to that manager's own reportees, so "Team" is the truth of what
 * they see, not just the label.
 */
const TEAM_NAV: NavItem[] = [
  // The real dashboard, not the cut-down team page: /api/admin/dashboard
  // already narrows a reporting manager to their own subordinates, so they get
  // the full view of exactly their team.
  { label: 'Dashboard',       to: '/andone/admin/dashboard',       icon: BarChart2,     permission: 'requests:view-team' },
  { label: 'Requests',        to: '/andone/admin/requests',        icon: Inbox,         permission: 'requests:view-team' },
  { label: 'Mark Attendance', to: '/andone/admin/mark-attendance', icon: CalendarClock, permission: 'requests:view-team' },
];

const HR_ADMIN_NAV: NavItem[] = [
  { label: 'Dashboard',        to: '/andone/admin/dashboard',       icon: BarChart2,    permission: 'dashboard:view', hr: true },
  { label: 'Attendance',       to: '/andone/org-overview',          icon: ClipboardList, permission: 'attendance:view', hr: true },
];

const ADMIN_NAV: NavItem[] = [
  { label: 'Requests',         to: '/andone/admin/requests',         icon: Inbox,       permission: 'requests:view', hr: true },
  { label: 'Mark Attendance',  to: '/andone/admin/mark-attendance',  icon: CalendarClock, permission: 'attendance:edit', hr: true },
  { label: 'User Management',  to: '/andone/admin/users',            icon: Briefcase,   permission: 'users:view', hr: true },
  { label: 'Leave Balances',   to: '/andone/admin/leave-balances',   icon: Scale,       permission: 'users:view', hr: true },
  { label: 'Leave Settings',   to: '/andone/admin/leave-settings',   icon: SlidersHorizontal, permission: 'users:view', hr: true },
  { label: 'Org Chart',        to: '/andone/admin/org-chart',        icon: GitBranch,   permission: 'users:view' },
  { label: 'Holidays',         to: '/andone/admin/holidays',         icon: CalendarDays, permission: 'holidays:view', hr: true },
  { label: 'Inventory',        to: '/andone/admin/inventory',        icon: Boxes,       permission: 'inventory:view', hr: true },
  { label: 'Locations',        to: '/andone/admin/locations',        icon: MapPin,      permission: 'locations:view' },
  { label: 'Audit Log',        to: '/andone/admin/audit-log',        icon: ShieldCheck, permission: 'audit-log:view' },
  { label: 'Email Log',        to: '/andone/admin/email-log',        icon: Mail,        permission: 'email-log:view' },
  { label: 'Settings',         to: '/andone/admin/settings',         icon: Settings,    permission: 'notification-settings:view' },
];

/** The HR view's sidebar — one list, the hr-flagged entries of both staff sets. */
export const HR_VIEW_NAV: NavItem[] = [...HR_ADMIN_NAV, ...ADMIN_NAV].filter(i => i.hr);

/**
 * Permissions the sidebar reads, including the ones synthesised from a user's
 * standing rather than granted by name.
 */
export function effectivePermissionsFor(user: {
  permissions?: string[] | null; roles?: string[] | null;
  isReportingManager?: boolean; canSelfApprove?: boolean; canApproveAll?: boolean;
  isBookingApprover?: boolean;
} | null | undefined): string[] {
  const roles = user?.roles ?? [];
  return [
    ...(user?.permissions ?? []),
    ...(user?.isReportingManager || user?.canSelfApprove || user?.canApproveAll ? ['requests:view-team'] : []),
    ...(roles.includes('Admin') || roles.includes('SuperAdmin') || roles.includes('HR') ? ['booking:manage', 'booking:approve'] : []),
    ...(user?.permissions?.includes('booking:approve') || user?.isBookingApprover ? ['booking:approve'] : []),
  ];
}

/** The roles the HR view belongs to. Standing alone does not earn it. */
const HR_VIEW_ROLES = ['Admin', 'SuperAdmin', 'HR', 'AdminViewer'];

/**
 * Whether this account gets the HR view — the role decides first, the
 * permissions only decide whether it would have anything in it.
 *
 * Permissions alone are not enough, because some are synthesised from a
 * person's standing rather than granted: `withApprovalPermissions` hands a
 * global approver or reporting manager `requests:view`, `dashboard:view` and
 * `attendance:view` so they can act on their own approvals. Those are exactly
 * the permissions behind the HR pages, so reading permissions first handed the
 * HR toggle to any approver — an employee with `can_approve_all` among them.
 */
export function hasHrPages(user: {
  roles?: string[] | null;
  permissions?: string[] | null;
  isReportingManager?: boolean; canSelfApprove?: boolean; canApproveAll?: boolean;
  isBookingApprover?: boolean;
} | null | undefined): boolean {
  const roles = user?.roles ?? [];
  if (!roles.some((r) => HR_VIEW_ROLES.includes(r))) return false;
  const permissions = effectivePermissionsFor(user);
  // A nav item with no permission of its own is not a reason to open the view.
  return HR_VIEW_NAV.some((i) => i.permission != null && permissions.includes(i.permission));
}

const ANDBOOK_MAIN_NAV: NavItem[] = [
  { label: 'Home',          to: '/andbook/dashboard',   icon: LayoutDashboard },
  { label: 'Book a space',  to: '/andbook/book',        icon: Search },
  { label: 'My bookings',   to: '/andbook/my-bookings', icon: BookOpen },
  { label: 'Calendar',      to: '/andbook/calendar',    icon: CalendarDays },
];

const ANDBOOK_ADMIN_NAV: NavItem[] = [
  { label: 'All Bookings',  to: '/andbook/all-bookings', icon: ListChecks,     permission: 'booking:approve' },
  { label: 'Approvals',     to: '/andbook/approvals',    icon: ClipboardCheck, permission: 'booking:approve' },
  { label: 'SOS console',   to: '/andbook/sos',          icon: Siren,          permission: 'booking:sos' },
  { label: 'Settings',      to: '/andbook/settings',     icon: Settings,       permission: 'booking:manage' },
];

const REQUESTS_PATH = '/andone/admin/requests';

/**
 * Points the Requests entry at the Pending filter while anything is waiting on
 * this user, so the badge and where it leads agree. NavLink matches on the
 * pathname alone, so the entry still highlights as active either way.
 */
function withPendingLink(items: NavItem[], pending: number): NavItem[] {
  if (pending <= 0) return items;
  return items.map((i) => (i.to === REQUESTS_PATH ? { ...i, to: PENDING_REQUESTS_PATH } : i));
}

const NAV_TOUR_IDS: Record<string, string> = {
  '/andone/dashboard':   'nav-home',
  '/andone/my-records':  'nav-my-records',
  '/andone/my-calendar': 'nav-my-calendar',
  '/andone/my-requests': 'nav-my-requests',
  '/andone/passkeys':    'nav-passkeys',
};

function NavBadge({ count, danger }: { count: number; danger?: boolean }) {
  return (
    <span style={{
      minWidth: 18, height: 18, padding: '0 5px',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 11, fontWeight: 800, lineHeight: 1,
      color: danger ? '#fff' : 'var(--brand-navy-dark)',
      background: danger ? '#c0392b' : 'var(--brand-orange)',
      borderRadius: 20,
    }}>
      {count > 99 ? '99+' : count}
    </span>
  );
}

function NavItemRow({ item, collapsed, onNavClick, badge, badgeDanger }: {
  item: NavItem;
  collapsed: boolean;
  onNavClick?: () => void;
  badge?: number;
  badgeDanger?: boolean;
}) {
  const [tooltipY, setTooltipY] = useState<number | null>(null);
  const showBadge = !!badge && badge > 0;

  return (
    <div style={{ position: 'relative' }}>
      <NavLink
        to={item.to}
        onClick={onNavClick}
        onMouseEnter={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          setTooltipY(rect.top + rect.height / 2);
        }}
        onMouseLeave={() => setTooltipY(null)}
        {...(NAV_TOUR_IDS[item.to] ? { 'data-tour': NAV_TOUR_IDS[item.to] } : {})}
        className={({ isActive }) =>
          cn(
            'flex items-center font-medium mb-1.5',
            isActive
              ? 'text-white'
              : 'text-white/95 hover:text-white hover:bg-white/[0.06]',
          )
        }
        style={({ isActive }) => ({
          fontSize: 14.5,
          borderRadius: isActive ? 6 : 8,
          padding: '9px 12px',
          justifyContent: 'flex-start',
          gap: 10,
          background: isActive ? 'var(--brand-orange)' : undefined,
          color: isActive ? '#fff' : undefined,
        })}
      >
        {({ isActive }) => (
          <>
            <span style={{ position: 'relative', flexShrink: 0, display: 'inline-flex' }}>
              <item.icon style={{ width: 18, height: 18, opacity: isActive ? 1 : 0.8 }} />
              {/* When collapsed, show a tiny dot on the icon instead of the full pill */}
              {collapsed && showBadge && (
                <span style={{
                  position: 'absolute', top: -4, right: -5,
                  minWidth: 8, height: 8, borderRadius: 20,
                  background: badgeDanger ? '#c0392b' : 'var(--brand-orange)',
                  border: '2px solid var(--sidebar-bg)',
                }} />
              )}
            </span>
            <span style={{
              whiteSpace: 'nowrap',
              opacity: collapsed ? 0 : 1,
              transition: `opacity 0.16s ease`,
              pointerEvents: 'none',
            }}>
              {item.label}
            </span>
            {!collapsed && showBadge && (
              <span style={{ marginLeft: 'auto', pointerEvents: 'none' }}>
                <NavBadge count={badge!} danger={badgeDanger} />
              </span>
            )}
          </>
        )}
      </NavLink>

      {/* Tooltip — only when fully collapsed and hovered (desktop only) */}
      {collapsed && tooltipY !== null && (
        <div style={{
          position: 'fixed',
          top: tooltipY,
          left: 84,
          background: 'var(--brand-navy-dark)',
          color: '#fff',
          fontSize: 15.5,
          fontWeight: 600,
          padding: '6px 12px',
          borderRadius: 7,
          whiteSpace: 'nowrap',
          zIndex: 200,
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
          pointerEvents: 'none',
          transform: 'translateY(-50%)',
        }}>
          {item.label}
          <div style={{
            position: 'absolute',
            left: -4,
            top: '50%',
            transform: 'translateY(-50%)',
            width: 8, height: 8,
            background: 'var(--brand-navy-dark)',
            clipPath: 'polygon(100% 0, 100% 100%, 0 50%)',
          }} />
        </div>
      )}
    </div>
  );
}

function NavSection({ label, items, permissions, collapsed, onNavClick, badges, dangerBadges }: {
  label: string;
  items: NavItem[];
  permissions?: string[];
  collapsed: boolean;
  onNavClick?: () => void;
  badges?: Record<string, number>;
  dangerBadges?: Record<string, boolean>;
}) {
  const visible = items.filter(i => !i.permission || (permissions && permissions.includes(i.permission)));
  if (!visible.length) return null;

  return (
    <div style={{ paddingTop: 16, paddingBottom: 4, paddingLeft: 10, paddingRight: 10 }}>
      <div style={{
        height: 1,
        background: 'rgba(255,255,255,0.08)',
        marginBottom: 8,
        opacity: collapsed ? 1 : 0,
        transition: `opacity 0.18s ease`,
      }} />
      <p style={{
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '1.5px',
        textTransform: 'uppercase',
        color: 'rgba(255,255,255,0.55)',
        padding: '0 4px',
        margin: 0,
        marginBottom: 8,
        maxHeight: collapsed ? 0 : 20,
        opacity: collapsed ? 0 : 1,
        overflow: 'hidden',
        transition: `max-height ${DUR} ${EASE}, opacity 0.16s ease`,
      }}>
        {label}
      </p>

      {visible.map((item) => (
        <NavItemRow
          key={item.to} item={item} collapsed={collapsed} onNavClick={onNavClick}
          badge={badges?.[item.to]} badgeDanger={dangerBadges?.[item.to]}
        />
      ))}
    </div>
  );
}

interface SidebarProps {
  width: number;
  collapsedWidth: number;
  isMobile: boolean;
  mobileNavOpen: boolean;
  onMobileClose: () => void;
}

export function Sidebar({ width, isMobile, mobileNavOpen, onMobileClose }: SidebarProps): JSX.Element {
  const { user } = useAuth();
  const { collapsed, toggle, navView } = useSidebar();
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();

  const isAndBook = location.pathname.startsWith('/andbook');

  // Prefetch holidays for current year in the background for users who can manage holidays
  useEffect(() => {
    if (!user?.permissions?.includes('holidays:view')) return;
    const year = new Date().getFullYear();
    qc.prefetchQuery({ queryKey: ['admin', 'holidays', year], queryFn: () => listHolidays(year) });
  }, [user, qc]);

  const effectiveCollapsed = !isMobile && collapsed;

  // Includes the virtual permissions — e.g. team-level approvers see Requests.
  const effectivePermissions = effectivePermissionsFor(user);

  // The topbar's HR View swaps the sidebar over to the HR pages — the employee
  // routes come off while it is on. A saved view with nothing in it for this
  // account falls back to the employee routes rather than an empty sidebar.
  const hrView = navView === 'hr' && hasHrPages(user);
  // With it off the sidebar is role-aligned: an employee gets their own pages,
  // HR / Admin / SuperAdmin get those plus the full staff sections (still
  // filtered down to what each of them is permitted).
  const roles = user?.roles ?? [];
  const isStaff = roles.includes('Admin') || roles.includes('SuperAdmin') || roles.includes('HR') || roles.includes('AdminViewer');

  // AndBook admin nav counters — only fetched inside AndBook for the relevant roles.
  const canBookingManage = effectivePermissions.includes('booking:manage') || effectivePermissions.includes('booking:approve');
  const canSos = user?.permissions?.includes('booking:sos') ?? false;

  const { data: bookingDash } = useQuery({
    queryKey: ['booking', 'adminDashboard'],
    queryFn: getAdminBookingDashboard,
    enabled: isAndBook && canBookingManage,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
  const pendingApprovals = (bookingDash?.pending_requests?.length ?? 0) + (bookingDash?.pending_room_requests?.length ?? 0);

  const { data: sosInbox } = useQuery({
    queryKey: ['sos', 'inbox', false],
    queryFn: () => getSosInbox(false),
    enabled: isAndBook && canSos,
    refetchInterval: 20_000,
    staleTime: 8_000,
  });
  const activeSos = sosInbox?.alerts?.length ?? 0;

  const andbookBadges: Record<string, number> = {
    '/andbook/approvals': pendingApprovals,
    '/andbook/sos': activeSos,
  };
  const andbookDangerBadges: Record<string, boolean> = { '/andbook/sos': true };

  // Requests waiting on this user's approval, shown on the Requests entry.
  const pendingApprovalCount = usePendingApprovalCount();
  const andoneBadges: Record<string, number> = { [PENDING_REQUESTS_PATH]: pendingApprovalCount };

  return (
    <aside
      data-tour="sidebar"
      className="fixed flex flex-col"
      style={{
        top: 0,
        bottom: 0,
        left: 0,
        width: isMobile ? 248 : width,
        zIndex: isMobile ? 50 : 20,
        background: 'var(--sidebar-bg)',
        borderRadius: 0,
        boxShadow: 'none',
        transform: isMobile
          ? mobileNavOpen ? 'translateX(0)' : 'translateX(-100%)'
          : undefined,
        transition: isMobile
          ? `transform ${DUR} ${EASE}`
          : `width ${DUR} ${EASE}`,
        overflow: 'hidden',
        willChange: isMobile ? 'transform' : 'width',
        cursor: effectiveCollapsed ? 'pointer' : 'default',
      }}
      onClick={() => { if (effectiveCollapsed) toggle(); }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-center flex-shrink-0"
        style={{
          height: 'var(--topbar-height)',
          background: 'var(--color-surface)',
          borderBottom: '3px solid var(--brand-orange)',
          overflow: 'hidden',
          padding: '0 12px',
          gap: 8,
        }}
      >
        {(!effectiveCollapsed) && (
          <div
            onClick={(e) => {
              e.stopPropagation();
              if (isMobile) onMobileClose();
              navigate(isAndBook ? '/andbook/dashboard' : user?.roles?.includes('Employee') ? '/andone/dashboard' : '/andone/admin/dashboard');
            }}
            style={{
              background: 'var(--logo-bg)',
              borderRadius: 'var(--logo-radius)',
              padding: 'var(--logo-padding)',
              marginRight: 'auto',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              transition: 'background 0.2s',
            }}
          >
            <img
              src="/logo.webp"
              alt="AndPayments"
              style={{ width: 100, height: 'auto', display: 'block' }}
            />
          </div>
        )}

        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (isMobile) onMobileClose();
            else toggle();
          }}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: 32, height: 32,
            padding: '4px', borderRadius: 6,
            background: 'transparent', border: 'none', cursor: 'pointer',
          }}
        >
          {isMobile
            ? <X style={{ width: 20, height: 20, color: 'var(--brand-navy)' }} />
            : collapsed
              ? <PanelLeftOpen  style={{ width: 20, height: 20, color: 'var(--brand-navy)' }} />
              : <PanelLeftClose style={{ width: 20, height: 20, color: 'var(--brand-navy)' }} />
          }
        </button>
      </div>

      {/* Nav */}
      <div
        className="flex-1 overflow-y-auto overflow-x-hidden"
        style={{ scrollbarWidth: 'none', paddingTop: 14, paddingBottom: 24 } as React.CSSProperties}
      >
        {isAndBook ? (
          <>
            <NavSection
              label="Main"   items={ANDBOOK_MAIN_NAV}   permissions={effectivePermissions}
              collapsed={effectiveCollapsed} onNavClick={isMobile ? onMobileClose : undefined}
            />
            {(user?.roles?.includes('Admin') || user?.roles?.includes('SuperAdmin') || user?.roles?.includes('HR') || user?.permissions?.includes('booking:approve') || user?.isBookingApprover) && (
              <NavSection
                label="Admin"  items={ANDBOOK_ADMIN_NAV}  permissions={effectivePermissions}
                collapsed={effectiveCollapsed} onNavClick={isMobile ? onMobileClose : undefined}
                badges={andbookBadges} dangerBadges={andbookDangerBadges}
              />
            )}
          </>
        ) : (
          <>
            {/* HR View replaces everything with the HR pages; with it off the
                sidebar follows the account's role. */}
            {hrView ? (
              <NavSection
                label="HR"       items={withPendingLink(HR_VIEW_NAV, pendingApprovalCount)} permissions={effectivePermissions}
                collapsed={effectiveCollapsed} onNavClick={isMobile ? onMobileClose : undefined}
                badges={andoneBadges}
              />
            ) : (
              <>
                <NavSection
                  label="Main"     items={MAIN_NAV}     permissions={effectivePermissions}
                  collapsed={effectiveCollapsed} onNavClick={isMobile ? onMobileClose : undefined}
                />
                {/* An approver who is not staff gets their team's pages here,
                    since the Overview and Admin sections below are role-only
                    and the HR view is no longer a way in. */}
                {!isStaff && effectivePermissions.includes('requests:view-team') && (
                  <NavSection
                    label="Team" items={withPendingLink(TEAM_NAV, pendingApprovalCount)} permissions={effectivePermissions}
                    collapsed={effectiveCollapsed} onNavClick={isMobile ? onMobileClose : undefined}
                    badges={andoneBadges}
                  />
                )}
                {isStaff && (
                  <>
                    <NavSection
                      label="Overview" items={HR_ADMIN_NAV} permissions={effectivePermissions}
                      collapsed={effectiveCollapsed} onNavClick={isMobile ? onMobileClose : undefined}
                    />
                    <NavSection
                      label="Admin"    items={withPendingLink(ADMIN_NAV, pendingApprovalCount)}    permissions={effectivePermissions}
                      collapsed={effectiveCollapsed} onNavClick={isMobile ? onMobileClose : undefined}
                      badges={andoneBadges}
                    />
                  </>
                )}
              </>
            )}
            <div style={{ paddingLeft: 8, paddingRight: 8, paddingTop: 4 }}>
              <NavItemRow
                item={{ label: 'FAQ', to: '/andone/faq', icon: HelpCircle }}
                collapsed={effectiveCollapsed}
                onNavClick={isMobile ? onMobileClose : undefined}
              />
            </div>
          </>
        )}
      </div>

      {/* Version footer — hidden in AndBook */}
      {!isAndBook && (
      <NavLink
        to={'/andone/whats-new'}
        onClick={(e) => {
          if (effectiveCollapsed) e.stopPropagation();
          if (isMobile) onMobileClose();
        }}
        className="group"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: effectiveCollapsed ? 'center' : 'flex-start',
          gap: 10,
          padding: effectiveCollapsed ? '14px 0' : '14px 18px',
          borderTop: '1px solid rgba(255,255,255,0.08)',
          textDecoration: 'none',
          cursor: 'pointer',
          flexShrink: 0,
          transition: `padding ${DUR} ${EASE}, background 0.2s`,
        }}
      >
        {/* Version pill */}
        {effectiveCollapsed ? (
          <Megaphone style={{ width: 14, height: 14, color: 'var(--brand-orange)', flexShrink: 0 }} />
        ) : (
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            fontSize: 11,
            fontWeight: 700,
            color: 'var(--brand-orange)',
            background: 'rgba(245,163,0,0.12)',
            border: '1px solid rgba(245,163,0,0.2)',
            padding: '3px 8px',
            borderRadius: 6,
            letterSpacing: 0.3,
            flexShrink: 0,
            whiteSpace: 'nowrap',
          }}>
            <Megaphone style={{ width: 11, height: 11 }} />
            v{APP_VERSION}
          </span>
        )}

        {/* "What's New" label */}
        {!effectiveCollapsed && (
          <span
            className="group-hover:text-white/70"
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: 'rgba(255,255,255,0.4)',
              whiteSpace: 'nowrap',
              transition: `color 0.2s`,
            }}
          >
            What's New
          </span>
        )}
      </NavLink>
      )}
    </aside>
  );
}
